import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ChevronDown, Zap, Link as LinkIcon, Music, Mic2, MonitorPlay, DollarSign,
  Settings, Headphones, Upload, Target, BarChart3, Globe, Star, MessageCircle,
  Play, ArrowRight, Radio, Crown
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const HERO_BG = "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxkaiUyMHR1cm50YWJsZSUyMGhhbmRzJTIwcGxheWluZyUyMG5lb258ZW58MHx8fHwxNzc1NTk5NDA1fDA&ixlib=rb-4.1.0&q=85";
const DASH_PREVIEW = "https://customer-assets.emergentagent.com/job_625bc63c-c133-4f56-b01c-9e034c827bdc/artifacts/3o7l7728_image.png";
const MUSIC_LIBRARY_IMG = "https://customer-assets.emergentagent.com/job_625bc63c-c133-4f56-b01c-9e034c827bdc/artifacts/j6g5oul5_image.png";
const SIGNUP_PREVIEW = "https://customer-assets.emergentagent.com/job_625bc63c-c133-4f56-b01c-9e034c827bdc/artifacts/6s7pl62s_image.png";
const MONETIZATION_BG = "https://customer-assets.emergentagent.com/job_625bc63c-c133-4f56-b01c-9e034c827bdc/artifacts/oaz4y6g6_image.png";

// ─── Accordion Item ───
const GuideAccordion = ({ num, icon: Icon, title, children, image, isOpen, onToggle, isHighlight }) => {
  const numStr = String(num).padStart(2, "0");

  if (isHighlight) {
    return (
      <div
        className="relative rounded-3xl p-[2px] overflow-hidden"
        style={{ background: "linear-gradient(135deg, #FF5100, #FFD600)", boxShadow: "0 0 50px rgba(255,81,0,0.15)" }}
        data-testid={`guide-section-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="absolute top-6 right-6 bg-[#FF5100] text-white text-[10px] font-bold px-4 py-2 rounded-full tracking-wider uppercase z-10" data-testid="most-important-badge">
          Most Important
        </div>
        <div className="bg-[#07070A] rounded-[22px] relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `url(${MONETIZATION_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          <button onClick={onToggle} className="relative w-full text-left px-8 md:px-12 py-8 md:py-10 flex items-center gap-5 group">
            <span className="absolute -top-2 -left-2 text-[80px] md:text-[100px] font-black text-white/[0.04] leading-none select-none pointer-events-none">{numStr}</span>
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0 relative z-10">
              <Icon className="w-6 h-6 text-orange-400" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white group-hover:text-orange-400 transition-colors flex-1 relative z-10">{title}</h3>
            <ChevronDown className={`w-6 h-6 text-white/40 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden relative"
              >
                <div className="px-8 md:px-12 pb-10 md:pb-12">
                  <div className="grid md:grid-cols-2 gap-8 items-start">
                    <div className="space-y-5 relative z-10">{children}</div>
                    {image && (
                      <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl hidden md:block">
                        <img src={image} alt="" className="w-full h-64 object-cover" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07070A] to-transparent opacity-40" />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#12121A] border border-white/[0.06] rounded-2xl overflow-hidden hover:border-white/10 transition-colors" data-testid={`guide-section-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <button onClick={onToggle} className="w-full text-left px-8 md:px-12 py-7 md:py-8 flex items-center gap-5 group relative">
        <span className="absolute -top-2 -left-1 text-[70px] md:text-[90px] font-black text-white/[0.03] leading-none select-none pointer-events-none">{numStr}</span>
        <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 relative z-10">
          <Icon className="w-5 h-5 text-white/60 group-hover:text-[#FF5100] transition-colors" />
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-white group-hover:text-[#FF5100] transition-colors flex-1 relative z-10">{title}</h3>
        <ChevronDown className={`w-5 h-5 text-white/30 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-8 md:px-12 pb-8 md:pb-10">
              <div className={`${image ? "grid md:grid-cols-2 gap-8 items-start" : ""}`}>
                <div className="space-y-5">{children}</div>
                {image && (
                  <div className="relative rounded-2xl overflow-hidden border border-white/[0.06] shadow-xl hidden md:block">
                    <img src={image} alt="" className="w-full h-56 object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#12121A] to-transparent opacity-50" />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Step = ({ num, title, children }) => (
  <div className="flex gap-4">
    <div className="w-8 h-8 rounded-full bg-[#FF5100]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
      <span className="text-[#FF5100] text-xs font-bold">{num}</span>
    </div>
    <div>
      <p className="text-white font-semibold">{title}</p>
      <p className="text-slate-400 text-sm mt-1 leading-relaxed">{children}</p>
    </div>
  </div>
);

const Tip = ({ children }) => (
  <div className="bg-[#FF5100]/5 border border-[#FF5100]/15 rounded-xl px-5 py-3.5 text-orange-300/80 text-sm">
    <span className="font-bold text-[#FF5100]">Pro Tip:</span> {children}
  </div>
);

// ─── Data ───
const ownerSections = [
  {
    icon: Zap, title: "Getting Started", image: SIGNUP_PREVIEW,
    content: (
      <>
        <Step num="1" title="Create Your Account">Sign up at djsandmcmedia.com with your email or Google account. Choose a plan — Starter (free), Pro, or Premium.</Step>
        <Step num="2" title="Choose Your Plan">Starter is free to explore. Pro adds white-label branding, custom domains, and higher bitrate. Premium gives you everything.</Step>
        <Step num="3" title="Complete Your Profile">Go to Dashboard &gt; Settings — fill in your station name, logo, tagline, and social links. This powers your public pages and player.</Step>
        <Tip>Complete the onboarding checklist on your Dashboard Overview to unlock all features fast.</Tip>
      </>
    ),
  },
  {
    icon: LinkIcon, title: "Connect AzuraCast",
    content: (
      <>
        <Step num="1" title="Get Your AzuraCast API Key">Log into your AzuraCast panel. Go to My Account &gt; API Keys and create a new key with read access.</Step>
        <Step num="2" title="Enter Station Details">In Dashboard &gt; My Channel, paste your AzuraCast URL, Station ID, and API key. The platform auto-detects your stream.</Step>
        <Step num="3" title="Verify Connection">Check Dashboard &gt; Station Health to confirm your stream is connected and data is flowing.</Step>
        <Tip>Using your own Icecast/Shoutcast? Use the External Stream option in Settings instead.</Tip>
      </>
    ),
  },
  {
    icon: Music, title: "Upload & Manage Music", image: MUSIC_LIBRARY_IMG,
    content: (
      <>
        <Step num="1" title="Music Library">Go to Dashboard &gt; Music Library to browse your AzuraCast media. Uploads sync automatically.</Step>
        <Step num="2" title="AutoDJ Rotation">AzuraCast's AutoDJ handles rotation automatically. Organize tracks by genre, time slot, or rotation weight.</Step>
        <Step num="3" title="Incoming Submissions">Artists submit tracks via the Submit page. Review them in Dashboard &gt; Submissions — approve, reject, or configure targeting.</Step>
      </>
    ),
  },
  {
    icon: Mic2, title: "Go Live",
    content: (
      <>
        <Step num="1" title="Get DJ Credentials">Go to Dashboard &gt; Broadcasting for your streaming server URL, port, mount point, and password.</Step>
        <Step num="2" title="Configure Software">Use OBS Studio, BUTT, Mixxx, or SAM Broadcaster. Paste your server URL and credentials.</Step>
        <Step num="3" title="Start Streaming">Hit "Start Streaming" — your Dashboard shows "LIVE DJ" status. AutoDJ pauses automatically.</Step>
        <Step num="4" title="Schedule Shows">Set up recurring shows in Dashboard &gt; Shows so listeners know when you're on air.</Step>
      </>
    ),
  },
  {
    icon: MonitorPlay, title: "Player & Embed Setup", image: DASH_PREVIEW,
    content: (
      <>
        <Step num="1" title="White Label Player">Configure your branded player in Dashboard &gt; White Label — colors, logo, chat, visualizer, video, weather widget.</Step>
        <Step num="2" title="Station Page">Build a public landing page in Dashboard &gt; Page Builder with hero images, about section, and embedded player.</Step>
        <Step num="3" title="Embed Widgets">Get embed codes from Dashboard &gt; Embed Hub to place your player on any website or blog.</Step>
        <Step num="4" title="Custom Domain">Point your own domain via Dashboard &gt; Domains. Available on Pro and Premium plans.</Step>
      </>
    ),
  },
  {
    icon: DollarSign, title: "Monetization", isHighlight: true, image: MONETIZATION_BG,
    content: (
      <>
        <Step num="1" title="Ad Bookings">Businesses book ad slots through the Advertise page. Self-serve checkout with PayPal — you approve and schedule.</Step>
        <Step num="2" title="Premium Artist Submissions">Artists pay for featured placement ($10) and priority targeting. Revenue flows directly through PayPal.</Step>
        <Step num="3" title="Affiliate Program">Earn commissions referring new station owners. Track clicks and conversions in real-time from your Dashboard.</Step>
        <Tip>Combine ad bookings + premium submissions + affiliate commissions for three revenue streams from one station.</Tip>
      </>
    ),
  },
  {
    icon: Settings, title: "Settings & Branding",
    content: (
      <>
        <Step num="1" title="Station Branding">Upload logo, set primary colors, configure tagline. Carries across your player, station page, and embeds.</Step>
        <Step num="2" title="Submission Preferences">Control accepted genres, BPM range, and explicit content in Settings &gt; Submission Targeting Preferences.</Step>
        <Step num="3" title="External Stream (BYOS)">Already have Icecast/Shoutcast? Connect in Settings &gt; External Stream Connection.</Step>
      </>
    ),
  },
  {
    icon: Headphones, title: "Music Licensing & DMCA",
    content: (
      <>
        <Step num="1" title="Network License">DJsandMCMedia holds a blanket ASCAP license. All channels are covered — no individual license needed.</Step>
        <Step num="2" title="DMCA Compliance">Max 3 songs/album or 4 songs/artist per 3-hour block. Don't pre-announce specific songs.</Step>
        <Step num="3" title="Your Responsibility">Keep metadata accurate. Don't stream unlicensed content. DMCA registered: DMCA-1070621.</Step>
      </>
    ),
  },
];

const artistSections = [
  {
    icon: Upload, title: "How to Submit Music", image: SIGNUP_PREVIEW,
    content: (
      <>
        <Step num="1" title="Go to the Submit Page">Click "Submit Music" in the navigation. No account required for basic submissions.</Step>
        <Step num="2" title="Choose Submission Type">Submit as an Artist, Promoter, Label, or Manager. Each type has tailored fields.</Step>
        <Step num="3" title="Fill In Track Details">Artist name, track title, genre, email, and a downloadable link (Google Drive, Dropbox, WeTransfer).</Step>
        <Step num="4" title="Select Your PRO">Choose ASCAP, BMI, SESAC, or Rights-Cleared for royalty tracking.</Step>
        <Tip>Include a short message about your track — it helps station owners decide faster.</Tip>
      </>
    ),
  },
  {
    icon: Target, title: "Targeting System",
    content: (
      <>
        <Step num="1" title="General Pool (Free)">Auto-matched to stations based on genre, BPM, and content preferences. Reaches all compatible stations.</Step>
        <Step num="2" title="Target Stations (Premium)">Pick specific stations — your track gets priority placement with a "Priority" badge in their queue.</Step>
        <Step num="3" title="BPM & Explicit Tags">Adding BPM and marking explicit content helps auto-matching find the right stations.</Step>
        <Tip>Targeting specific stations means your track jumps to the top of their queue. This is your competitive edge.</Tip>
      </>
    ),
  },
  {
    icon: BarChart3, title: "Track Your Status", image: DASH_PREVIEW,
    content: (
      <>
        <Step num="1" title="Email Notifications">You'll get notified when stations approve or review your track.</Step>
        <Step num="2" title="Status Types"><strong className="text-white">Pending</strong> — Under review. <strong className="text-white">Approved</strong> — In rotation. <strong className="text-white">Featured</strong> — Highlighted on Discover.</Step>
        <Step num="3" title="Airplay Tracking">Once approved, your plays and listener reach are tracked automatically across all stations.</Step>
      </>
    ),
  },
  {
    icon: Globe, title: "Exposure & Discovery",
    content: (
      <>
        <Step num="1" title="Discover Page">Approved tracks appear on the public Discover page where listeners browse trending music and stations.</Step>
        <Step num="2" title="Multi-Station Reach">One submission, maximum exposure — the targeting system sends to multiple stations simultaneously.</Step>
        <Step num="3" title="Artist Profile">Build your presence with an Artist Profile page showcasing tracks, airplay, and station features.</Step>
      </>
    ),
  },
  {
    icon: Star, title: "Premium Submissions", isHighlight: true,
    content: (
      <>
        <Step num="1" title="Featured Tier ($10)">Priority review, Featured badge on Discover, top placement when approved. One-time payment.</Step>
        <Step num="2" title="Artist Membership ($19.99/mo)">Unlimited submissions, airplay analytics, profile page, priority support, and all premium targeting.</Step>
        <Step num="3" title="Station Targeting">Select exactly which stations receive your track. Priority badge in their queue — they see you first.</Step>
        <Tip>Featured is a one-time boost. Artist Membership is the long-term play for ongoing exposure.</Tip>
      </>
    ),
  },
];

const SetupGuide = () => {
  const [activeTab, setActiveTab] = useState("owner");
  const [searchQuery, setSearchQuery] = useState("");
  const [openSection, setOpenSection] = useState(0);

  const sections = activeTab === "owner" ? ownerSections : artistSections;
  const filtered = searchQuery.trim()
    ? sections.filter(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : sections;

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
    setOpenSection(0);
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#07070A" }}>
      <SEO title="Setup Guide | DJsandMCMedia" description="Launch your station. Own your sound. Everything you need to get started." />

      {/* Grain overlay */}
      <div className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")", backgroundRepeat: "repeat" }} />

      <Header />

      {/* ─── HERO ─── */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden" data-testid="guide-hero">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07070A] via-[#07070A]/80 to-[#07070A]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07070A]/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-20 md:pb-28 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5100] mb-5" data-testid="guide-overline">
              DJsandMCMedia Setup Guide
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-6" data-testid="guide-heading">
              Launch Your Station.<br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #FF5100, #FFD600)" }}>
                Own Your Sound.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-xl leading-relaxed mb-10">
              DJs, Artists, and Creators are building real platforms — not just playlists. Here's everything you need to get started.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/#pricing" className="inline-flex items-center gap-3 bg-[#FF5100] hover:bg-[#FF6B26] text-white font-bold h-14 px-8 rounded-full hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(255,81,0,0.4)] transition-all duration-300" data-testid="guide-cta-start">
                <Radio className="w-5 h-5" /> Start Your Station
              </Link>
              <a href="#guide-content" className="inline-flex items-center gap-3 border border-white/20 bg-white/5 backdrop-blur-md text-white font-bold h-14 px-8 rounded-full hover:bg-white/10 transition-all duration-300" data-testid="guide-cta-learn">
                <Play className="w-4 h-4" /> See How It Works
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── GUIDE CONTENT ─── */}
      <section id="guide-content" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">

          {/* Tab Switcher — pill style */}
          <div className="flex justify-center mb-12">
            <div className="bg-[#12121A] p-2 rounded-full inline-flex border border-white/[0.06]" data-testid="guide-tab-switcher">
              <button
                onClick={() => handleTabSwitch("owner")}
                className={`px-8 py-3 rounded-full font-bold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeTab === "owner" ? "bg-[#FF5100] text-white shadow-lg" : "text-slate-400 hover:text-white"
                }`}
                data-testid="guide-tab-owner"
              >
                <Headphones className="w-4 h-4" /> Station Owners
              </button>
              <button
                onClick={() => handleTabSwitch("artist")}
                className={`px-8 py-3 rounded-full font-bold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeTab === "artist" ? "bg-[#FF5100] text-white shadow-lg" : "text-slate-400 hover:text-white"
                }`}
                data-testid="guide-tab-artist"
              >
                <Music className="w-4 h-4" /> Artists
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-lg mx-auto mb-14">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" />
            <input
              data-testid="guide-search"
              type="text"
              placeholder="Search guides..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setOpenSection(-1); }}
              className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-14 pr-6 py-4 text-white placeholder-white/25 focus:outline-none focus:border-[#FF5100]/40 focus:shadow-[0_0_20px_rgba(255,81,0,0.08)] transition-all duration-300 text-sm"
            />
          </div>

          {/* Sections */}
          <div className="space-y-4" data-testid="guide-sections">
            {filtered.map((section, i) => (
              <GuideAccordion
                key={section.title}
                num={i + 1}
                icon={section.icon}
                title={section.title}
                image={section.image}
                isHighlight={section.isHighlight}
                isOpen={openSection === i}
                onToggle={() => setOpenSection(openSection === i ? -1 : i)}
              >
                {section.content}
              </GuideAccordion>
            ))}
            {filtered.length === 0 && (
              <p className="text-center text-slate-500 py-16 text-lg">No guides found for "{searchQuery}"</p>
            )}
          </div>

          {/* ─── Bottom CTA ─── */}
          <div className="mt-20 text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Ready to build?</h2>
            <p className="text-slate-400 text-lg max-w-md mx-auto">Your station is waiting. Start free, upgrade when you're ready.</p>
            <div className="flex flex-wrap justify-center gap-4 pt-2">
              <Link to="/#pricing" className="inline-flex items-center gap-3 bg-[#FF5100] hover:bg-[#FF6B26] text-white font-bold h-14 px-8 rounded-full hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(255,81,0,0.4)] transition-all duration-300" data-testid="guide-bottom-cta">
                <Crown className="w-5 h-5" /> Start Your Station <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Floating Chat CTA ─── */}
      <button
        onClick={() => {
          const chatBubble = document.querySelector('[data-testid="chat-bubble"]');
          if (chatBubble) chatBubble.click();
        }}
        className="fixed bottom-6 right-20 z-40 bg-white/5 backdrop-blur-xl border border-white/10 text-white font-bold px-6 py-3 rounded-full hover:bg-white/10 transition-all duration-300 flex items-center gap-2 text-sm shadow-2xl"
        data-testid="guide-open-chat-btn"
      >
        <MessageCircle className="w-4 h-4 text-[#FF5100]" /> Chat with Us Live
      </button>

      <Footer />
    </div>
  );
};

export default SetupGuide;
